import { inject, Injectable } from "@angular/core";
import { AppData } from "src/app/data";
import { Title } from "@angular/platform-browser";
import { AlertController, ModalController } from "@ionic/angular";
import { Router } from "@angular/router";
import { DispatcherService, LocationUtilityService, NotificationService, RouteProviderService, TranslateService } from ".";
import { environment } from "src/environments/environment";
import { JobType, Dropoff, Job, Pickup, PreTripInspection, PostTripInspection, Break, PullOut, PullIn, _ScheduledFlagStop, Group, Route, StopTypeEnum, BaseJob } from "../types";
import { BuildServices, GenericRouteEvent } from "../types/normalize/generic-route-event.model";
import { PassService } from "./pass.service";
import { hasValidGeo } from "../helpers/ui-helpers";
import { LocationService } from "./location.service";
import { Waypoint } from "../types/waypoint.model";
import { OutOfServicePeriod } from "../types/normalize/out-out-of-service-period.model";
import { GroupTripMonitor } from "../types/normalize/group-trip-monitor.model";
import { CustomFieldInspection } from "../types/normalize/custom-field-inspection.model";

@Injectable({ providedIn: 'root' })
/** A service that normalizes the job types. */
export class NormalizeService {

    private readonly alertController = inject(AlertController);
    private readonly data = inject(AppData);
    private readonly browserTitle = inject(Title);
    private readonly dispatchSvc = inject(DispatcherService);
    private readonly locationSvc = inject(LocationService);
    private readonly modalController = inject(ModalController);
    private readonly notificationSvc = inject(NotificationService);
    private readonly passSvc = inject(PassService);
    private readonly routeProviderSvc = inject(RouteProviderService);
    private readonly router = inject(Router);
    private readonly translateSvc = inject(TranslateService);

    services: BuildServices;

  constructor() {
    this.services = {
      alertController: this.alertController,
      appData: this.data,
      browserTitle: this.browserTitle,
      dispatchSvc: this.dispatchSvc,
      locationSvc: this.locationSvc,
      modalController: this.modalController,
      notificationSvc: this.notificationSvc,
      passSvc: this.passSvc,
      routeProviderSvc: this.routeProviderSvc,
      router: this.router,
      translateSvc: this.translateSvc,
    };
  }

  buildRoute(route: Route): GenericRouteEvent[] {

    if (!route) { return []; }

    const routeEvents: GenericRouteEvent[] = [];

    //  handle jobs
    if (environment.features.jobVersion === 1) { routeEvents.push(...this.handleJustJobs(route.jobs)); }
    if (environment.features.jobVersion === 2) { routeEvents.push(...this.handleAnyJobs(route));  }

    //  handle inspections
    if (route.preTrip && !route.preTrip.hasBeenCompleted) { routeEvents.push( new PreTripInspection(route.preTrip, this.services)); }
    if (route.postTrip && !route.postTrip.hasBeenCompleted) { routeEvents.push( new PostTripInspection(route.postTrip, this.services)); }
    if (route.customForm?.length > 0 && !route.customFormCompleted) {
      routeEvents.push( new CustomFieldInspection(route.customForm, this.services));
    }

    //  handle breaks
    if (route.break) { routeEvents.push(new Break(route.break, this.services)); }

    if (route?.scheduledFlagStops) {
      route.scheduledFlagStops.forEach(sfs => {
        routeEvents.push(new _ScheduledFlagStop(sfs, this.services));
      });
    }

    if (!environment.features.groupStopPerform) {
      return this.sortRouteEvents(routeEvents);
    }

    //  handle groups

    const groupedArray = routeEvents.reduce((acc, routeEvent) => {
      //  figure out if the job needs to be part of a group
      const needsGrouped = routeEvents.some(_re => this.groupMatch(routeEvent, _re));

      // if routeEvent does not need grouped, we are done here
      if (!needsGrouped) { return [ ...acc, routeEvent ]; }

      // routeEvent needs to be part of a group: add job to a group
      let group: Group | undefined = acc.find(_group => _group instanceof Group && this.groupMatch(routeEvent, _group)) as Group;
      if (group) {
        group.addRouteEvent(routeEvent);
        return acc;
      }
      group = new Group([routeEvent], this.services);
      return [ ...acc, group ];
    }, <GenericRouteEvent[]>[]);

    return this.sortRouteEvents(groupedArray);
  }

  /**
   * Compares the two by location and time, returns true if they are the same.
   * If either in the comparison does not have valid geo-coordinates (don't have any or null island),
   * than always returns false
   */
  private sameTimeAndLocation(a: GenericRouteEvent, b: GenericRouteEvent): boolean {
    if (!hasValidGeo(a.location) || !hasValidGeo(b.location)) { return false; }
    if (!a.scheduledStart || !b.scheduledStart) { return false; }

    const distance = LocationUtilityService.euclideanDistanceInMiles(
      a.location.coordinate.latitude,
      a.location.coordinate.longitude,
      b.location.coordinate.latitude,
      b.location.coordinate.longitude,
    );
    const timeDiff = Math.abs(a.scheduledStart.valueOf() - b.scheduledStart.valueOf());
    const { distanceTolerance, timeTolerance } = environment.features;
    return distance <= distanceTolerance && timeDiff <= timeTolerance * 60000;
  }

  private groupMatch(a: GenericRouteEvent, b: GenericRouteEvent): boolean {
    if (a.id === b.id) { return false; }
    const groupBySequence = environment.features.sortBy === 'stopSequence';
    if (groupBySequence) return a.stopSequence === b.stopSequence;
    return this.sameTimeAndLocation(a, b);
  }

  private sortRouteEvents(routeEvents: GenericRouteEvent[]): GenericRouteEvent[] {
    return routeEvents.sort((a, b) => {

      // because scheduled times for pre and post trip can be incongruent with the job list
      if (a instanceof PreTripInspection) { return -1; }
      if (b instanceof PreTripInspection) { return 1; }
      if (a instanceof PostTripInspection) { return 1; }
      if (b instanceof PostTripInspection) { return -1; }
      if (a instanceof CustomFieldInspection) { return 1; }
      if (b instanceof CustomFieldInspection) { return -1; }

      if (environment.features.sortBy === 'stopSequence') {
        return (a.stopSequence ?? Infinity) - (b.stopSequence ?? Infinity);
      }

      return (a.scheduledStart?.valueOf() ?? Infinity) - (b.scheduledStart?.valueOf() ?? Infinity);
    });
  }

  private handleJustJobs(jobs: BaseJob[]): GenericRouteEvent[] {
    if (!jobs?.length) { return []; }
    jobs = jobs.filter(job => !job.completed);
    const routeEvents = [];
    const { pickups, dropoffs } = jobs.reduce((acc, job) => {
      switch(job.jobType) {
        case JobType.Pickup: acc.pickups.push(job); break;
        case JobType.DropOff: acc.dropoffs.push(new Dropoff(job, this.services)); break;
        default: routeEvents.push(new Job(job, this.services));
      }
      return acc;
    }, {
      pickups: [],
      dropoffs: [],
    });
    pickups.forEach(job => {
      const dropoff = dropoffs.find(dropoff => dropoff.value.rideId === job.rideId);
      routeEvents.push(new Pickup(job, dropoff, this.services));
    });
    routeEvents.push(...dropoffs);
    return routeEvents;
  }

  private handleAnyJobs(route: Route): GenericRouteEvent[] {
    if (!route.jobs?.length) { return []; }
    const routeEvents = [];
    const justJobs = route.jobs.filter(routeEvent => routeEvent.stopType === StopTypeEnum.JOB || routeEvent.stopType === undefined);
    routeEvents.push(...this.handleJustJobs(justJobs));

    const theRest = route.jobs.filter(routeEvent => routeEvent.stopType !== StopTypeEnum.JOB);
    theRest.forEach(routeEvent => {
      switch(routeEvent.stopType) {
        case StopTypeEnum.SCHEDULED_FLAG_STOP:
          routeEvents.push(new _ScheduledFlagStop(routeEvent, this.services));
          break;
        case StopTypeEnum.PULL_IN:
          routeEvents.push(new PullIn(route, routeEvent, this.services));
          break;
        case StopTypeEnum.PULL_OUT:
          routeEvents.push(new PullOut(route, routeEvent, this.services));
          break;
        case StopTypeEnum.OUT_OF_SERVICE_START:
        case StopTypeEnum.OUT_OF_SERVICE_END:
          routeEvents.push(new OutOfServicePeriod(routeEvent, this.services));
          break;
        case StopTypeEnum.WAYPOINT:
          routeEvents.push(new Waypoint(routeEvent, this.services));
          break;
        case StopTypeEnum.MONITOR_PICKUP:
        case StopTypeEnum.MONITOR_DROPOFF:
          routeEvents.push(new GroupTripMonitor(routeEvent, this.services));
          break;
        case StopTypeEnum.BREAK_WITH_LOCATION:
        case StopTypeEnum.BREAK_WITHOUT_LOCATION:
          routeEvents.push(new Break(routeEvent, this.services));
          break;
        default:
          console.error('You forgot to implement me:', StopTypeEnum[routeEvent.stopType], routeEvent.stopType);
      }
    });

    return routeEvents;
  }

}
